import { Body, breakpoints, Image, Spacer, useAlert } from '@spritz-finance/ui'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { METHOD_FI_TOKEN_ERROR } from '../../analytics/events'
import loader from '../../assets/animations/spritz-logo-loop.gif'
import { Column } from '../../components/Layout/Column'
import { GlassCard } from '../../components/Layout/GlassCard'
import { CenteredPageInner, PageContainer } from '../../components/Layout/PageContainer'
import { PageLoading } from '../../components/PageLoading'
import { ThemedHeroImage } from '../../components/ThemedHeroImage'
import { useAnalytics } from '../../context/useAnalytics'
import { useBillsDataQuery, useMethodFiAuthTokenLazyQuery } from '../../graphql/generated/graphql'
import { useEntityStatus } from '../bills/components/useEntityStatus'
import { useMethodFiAuthentication } from './useMethodFiAuthentication'

const Loader = styled.img`
    width: 150px;
`

const FindBillsCard = styled(GlassCard)`
    width: 700px;
    ${breakpoints.small} {
        width: auto;
    }
`

const Searching = ({ token }: { token: string }) => {
    const addedBills = useRef<string[]>([])
    const billsFound = useRef(0)
    const handler = useRef<NodeJS.Timeout | null>(null)

    const alert = useAlert()
    const navigate = useNavigate()
    const { data, startPolling, stopPolling } = useBillsDataQuery()
    const bills = useMemo(() => data?.bills ?? [], [data])

    const getFoundBills = useCallback(() => {
        const billIds = bills.map((bill) => bill.accountId)
        return addedBills.current.filter((id) => billIds.includes(id)).length
    }, [bills])

    const onFindAllBills = useCallback(
        (found: number) => {
            if (found > 0) alert.success(`Successfully added your bills!`)
            navigate('/bills/main')
        },
        [alert, navigate]
    )

    const onError = useCallback(() => {
        alert.success(`There was an issue trying to find your bills`)
        navigate('/bills/main')
    }, [alert, navigate])

    const onSuccess = (accounts: string[]) => {
        const timeout = accounts.length > 0 ? 10000 : 0
        addedBills.current = accounts
        startPolling(1000)
        handler.current = setTimeout(() => {
            stopPolling()
            onFindAllBills(0)
        }, timeout)
    }

    const onCancel = () => {
        navigate('/bills/main')
    }

    useMethodFiAuthentication({ token, onSuccess, onCancel, onError })

    useEffect(() => {
        if (addedBills.current.length == 0) return

        const numBillsFound = getFoundBills()
        billsFound.current = numBillsFound

        if (numBillsFound === addedBills.current.length) {
            if (handler.current) clearTimeout(handler.current)
            stopPolling()
            onFindAllBills(numBillsFound)
        }
    }, [bills, getFoundBills, onFindAllBills, stopPolling])

    return (
        <FindBillsCard>
            <Column align="center">
                <Spacer height={24} />
                <ThemedHeroImage>
                    <Image.SchedulePayment height={75} />
                </ThemedHeroImage>
                <Spacer height={24} />
                <Body.Bold size={16}>Adding your new accounts!</Body.Bold>
                <Spacer height={16} />
                <Body.Med size={14} align="center">
                    Just wrapping things up. This should only take a moment.
                </Body.Med>
                <Spacer height={8} />
                <Loader src={loader} alt="Spritz logo loader" />
            </Column>
        </FindBillsCard>
    )
}

const FindBillsContent = () => {
    const { trackAnalyticsEvent } = useAnalytics()
    const alert = useAlert()
    const navigate = useNavigate()
    const [token, setToken] = useState<string | null>(null)
    const [fetchToken, { loading }] = useMethodFiAuthTokenLazyQuery({
        fetchPolicy: 'no-cache',
    })

    const handleFetchToken = async () => {
        const { data } = await fetchToken()
        const authToken = data?.getMethodFiAuthToken ?? null
        if (authToken === null) {
            trackAnalyticsEvent(METHOD_FI_TOKEN_ERROR)
            alert.success(`There was an issue trying to find your bills`)
            navigate('/bills/main')
        } else {
            setToken(authToken)
        }
    }
    useEffect(() => {
        handleFetchToken()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    if (loading || !token) return <PageLoading />

    return (
        <PageContainer>
            <CenteredPageInner $width={700}>
                <Searching token={token} />
            </CenteredPageInner>
        </PageContainer>
    )
}

export const FindMyBillsPage: React.VFC = () => {
    const initialCheck = useRef(false)
    const { canAutoFindBills, loading } = useEntityStatus()

    const navigate = useNavigate()

    useEffect(() => {
        if (loading || initialCheck.current) return
        if (!canAutoFindBills) navigate('/bills/main')
        initialCheck.current = true
    }, [canAutoFindBills, loading, navigate])

    if (loading) return <PageLoading />

    return <FindBillsContent />
}
